/* eslint-disable @next/next/no-img-element */
import Head from 'next/head';
import Link from 'next/link';
import { useState } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { Fade, Slide } from "react-awesome-reveal";
import YouTube from 'react-youtube';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Accordion, AccordionItem } from 'react-light-accordion';
import 'react-light-accordion/demo/css/index.css';
import FAQ from '../utils/faq.json';
import { AnimationIntegration, AnimationQrcode } from '../components/Lotties';
import FEATURES from '../utils/features.json';
import PriceCard from '../components/PriceCard';
import { AnimationPhone } from '../components/Lotties';
import swal from 'sweetalert';
import QRCode from 'qrcode';
import Downloads from '../views/Downloads';
import { FaDownload, FaArrowRight, FaCheck } from 'react-icons/fa';

export default function Home() {
  const priceplans = [];
  const [url, setUrl] = useState("");
  const [light, setLight] = useState("#FFFFFF");
  const [dark, setDark] = useState("#000000");

  const onQrcode = async () => {
    if (url.replace(/\s/gmi, '') == '') return;
    const qrcode = await QRCode.toDataURL(url, {
      width: 500, color: {
        dark: dark, light: light
      }
    });
    const result = await swal({
      title: `Your Code`,
      icon: qrcode,
      buttons: ['CANCEL', 'DOWNLOAD']
    });
    if (!result) return;

    const a = document.createElement('a');
    a.download = `lindle-qrcode.png`;
    a.href = qrcode;
    a.click();
    a.remove();
  }

  return (
    <div className="bg-gradient-to-b from-white via-green-50/30 to-white">
      <Head>
        <title>Lindle - All Your Links in One Place</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content="All your links, All at once, All in one place!" />
        <link rel="icon" href="/favicon.ico" />

        {/* Facebook */}
        <meta property="og:title" content="Lindle" />
        <meta property="og:description" content="All your links, All at once, All in one place!" />
        <meta property="og:image" content="/logoText.png" />
        <meta property="og:url" content="https://lindle.vercel.app" />
        <meta property="og:type" content="website" />

        <meta name="twitter:title" content="Lindle" />
        <meta name="twitter:description" content="All your links, All at once, All in one place!" />
        <meta name="twitter:image" content="/logoText.png" />
        <meta name="twitter:card" content="/bannerText.png" />

        {/* pinterest */}
        <meta name="pinterest-rich-pin" content="true" />
      </Head>

      <Navigation />

      <main className='w-full overflow-hidden'>

        {/* Hero Section */}
        <section className='relative min-h-[90vh] flex items-center'>
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-0 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-0 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl"></div>
          </div>

          <div className='container mx-auto px-6 lg:px-12 relative z-10'>
            <Slide>
              <div className='grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto'>
                
                {/* Left Content */}
                <div className='space-y-8 text-center lg:text-left'>
                  <div className="inline-block">
                    <span className='inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium'>
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Trusted by 100+ users
                    </span>
                  </div>

                  <h1 className='text-5xl lg:text-7xl font-bold text-gray-900 leading-tight'>
                    <TypeAnimation
                      sequence={[
                        'All your links',
                        1500,
                        'All at once',
                        1500,
                        'All in one place!',
                        1500,
                      ]}
                      speed={50}
                      repeat={Infinity}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"
                    />
                  </h1>
                  
                  <p className='text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0'>
                    Your ultimate solution for managing and organizing web links with ease. Save, categorize, and access your online resources like never before.
                  </p>

                  <div className='flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4'>
                    <Link 
                      href="/#downloads" 
                      className='group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-lg font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 hover:-translate-y-0.5'
                    >
                      <FaDownload className="group-hover:animate-bounce" />
                      <span>Get Lindle Free</span>
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                    
                    <Link 
                      href="#features" 
                      className='inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-green-700 text-lg font-semibold rounded-xl hover:bg-gray-50 transition-all duration-300 border-2 border-green-200 hover:border-green-300'
                    >
                      Learn More
                    </Link>
                  </div>

                  {/* Download badges */}
                  <div className='flex flex-wrap gap-4 justify-center lg:justify-start pt-8'>
                    <Link href="https://play.google.com/store/apps/details?id=com.m2kdevelopments.lindle" className="hover:opacity-80 transition-opacity">
                      <img width={180} alt="Google Play" src="googleplay.png" className="h-14 w-auto" />
                    </Link>
                    <Link href="https://chrome.google.com/webstore/detail/igkkojjaikfmiibedalhgmfnjohlhmaj" className="hover:opacity-80 transition-opacity">
                      <img width={180} alt="Chrome Store" src="chromestore.png" className="h-14 w-auto" />
                    </Link>
                  </div>
                </div>

                {/* Right Content - Phone Animation */}
                <div className='flex justify-center lg:justify-end'>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                    <AnimationPhone width="100%" />
                  </div>
                </div>

              </div>
            </Slide>
          </div>
        </section>

        {/* Features Showcase */}
        <section className='py-24 bg-gradient-to-br from-green-50 via-emerald-50 to-green-50'>
          <div className='container mx-auto px-6 lg:px-12'>
            <div className='text-center mb-16'>
              <h2 className='text-4xl lg:text-6xl font-bold text-gray-900 mb-6'>
                Why Choose Lindle?
              </h2>
              <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
                Experience the power of organized link management with features designed for productivity
              </p>
            </div>

            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto'>
              <Fade cascade damping={0.1}>
                <div className='group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-green-200 hover:-translate-y-2'>
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <FaCheck className="text-white text-2xl" />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 mb-4'>Effortless Link Saving</h3>
                  <p className='text-gray-600 leading-relaxed'>Save links from the web with a single click, and watch as they're neatly organized in one central hub.</p>
                </div>

                <div className='group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-purple-200 hover:-translate-y-2'>
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <FaCheck className="text-white text-2xl" />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 mb-4'>Intelligent Auto-Sorting</h3>
                  <p className='text-gray-600 leading-relaxed'>Automatically categorizes your saved links based on their source, whether from social media or other websites.</p>
                </div>

                <div className='group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-amber-200 hover:-translate-y-2'>
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <FaCheck className="text-white text-2xl" />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 mb-4'>Cross-Device Sync</h3>
                  <p className='text-gray-600 leading-relaxed'>Create custom folders to sort and manage your links. Keep everything neatly organized for quick access.</p>
                </div>
              </Fade>
            </div>
          </div>
        </section>

        {/* Downloads Section */}
        <Downloads />

        {/* Integrations */}
        <section className='py-24 relative'>
          <div className='container mx-auto px-6 lg:px-12'>
            <Slide>
              <div className='grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto'>
                <div className='space-y-6'>
                  <div className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                    Coming Soon
                  </div>
                  <h2 className='text-5xl lg:text-6xl font-bold text-gray-900'>
                    Powerful Integrations
                  </h2>
                  <p className='text-xl text-gray-600'>
                    Connect Lindle with your favorite tools and platforms
                  </p>
                  <ul className='space-y-4 text-lg'>
                    {['Bitly', 'Google Meets', 'Zoom', 'Zapier', 'Pabbly'].map(item => (
                      <li key={item} className='flex items-center gap-3 text-gray-700'>
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex justify-center">
                  <AnimationIntegration />
                </div>
              </div>
            </Slide>
          </div>
        </section>

        {/* Video Section */}
        <section className='py-24 bg-gray-50'>
          <div className='container mx-auto px-6 lg:px-12'>
            <div className='max-w-5xl mx-auto'>
              <div className='text-center mb-12'>
                <h2 className='text-4xl lg:text-5xl font-bold text-gray-900 mb-4'>
                  See Lindle in Action
                </h2>
                <p className='text-xl text-gray-600'>
                  Watch how easy it is to manage your links
                </p>
              </div>
              <div className='rounded-3xl overflow-hidden shadow-2xl'>
                <YouTube 
                  videoId="gCJd1Lu-a8k"
                  title="Lindle"
                  opts={{
                    height: '600',
                    width: '100%',
                    playerVars: {
                      autoplay: 0,
                      origin: process.env.NODE_ENV !== 'production' ? 'http://localhost:3000' : 'https://lindle.vercel.app'
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Features */}
        <section id="features" className='py-24'>
          <div className='container mx-auto px-6 lg:px-12'>
            <div className='text-center mb-16'>
              <h2 className='text-4xl lg:text-5xl font-bold text-gray-900 mb-4'>
                Everything You Need
              </h2>
              <p className='text-xl text-gray-600'>
                Powerful features to streamline your workflow
              </p>
            </div>

            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto'>
              {[
                {
                  title: 'Seamless Integration with URL Shorteners',
                  description: 'Enjoy the convenience of integrated Bitly and other popular URL shorteners to keep your links clean and concise.',
                  color: 'from-green-500 to-emerald-500'
                },
                {
                  title: 'Intuitive Search Functionality',
                  description: 'Easily find any link you\'ve saved by using our powerful search feature. No more endless scrolling or digging through folders.',
                  color: 'from-blue-500 to-cyan-500'
                },
                {
                  title: 'Folder Organization',
                  description: 'Create custom folders to sort and manage your links. Keep everything neatly tucked away for quick access.',
                  color: 'from-purple-500 to-pink-500'
                },
                {
                  title: 'Access from Messenger Platforms',
                  description: 'Our extension embeds directly into Facebook Messenger, Instagram, and LinkedIn. Access your saved links right from these platforms.',
                  color: 'from-amber-500 to-orange-500'
                },
                {
                  title: 'Share with Ease',
                  description: 'Share links with colleagues, friends, and family without leaving your preferred messenger platform.',
                  color: 'from-red-500 to-rose-500'
                },
                {
                  title: 'Clean, User-Friendly Interface',
                  description: 'The extension is designed for simplicity and ease of use, making link management a breeze.',
                  color: 'from-teal-500 to-cyan-500'
                }
              ].map((feature, idx) => (
                <Fade key={idx} delay={idx * 100}>
                  <div className='group bg-white p-8 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 border-l-4 border-transparent hover:border-green-500'>
                    <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-lg mb-6 group-hover:scale-110 transition-transform`}></div>
                    <h3 className='text-xl font-bold text-gray-900 mb-4'>{feature.title}</h3>
                    <p className='text-gray-600 leading-relaxed'>{feature.description}</p>
                  </div>
                </Fade>
              ))}
            </div>
          </div>
        </section>

        {/* QR Code Generator */}
        <section className='py-24 bg-gradient-to-br from-green-50 to-emerald-50'>
          <div className='container mx-auto px-6 lg:px-12'>
            <div className='max-w-5xl mx-auto'>
              <div className='text-center mb-12'>
                <h2 className='text-4xl lg:text-5xl font-bold text-gray-900 mb-4'>
                  Try Our QR Code Generator
                </h2>
                <p className='text-xl text-gray-600'>
                  Create custom QR codes for your links instantly
                </p>
              </div>

              <div className='bg-white rounded-3xl shadow-2xl p-8 lg:p-12'>
                <div className='grid lg:grid-cols-2 gap-12 items-center'>
                  <div className='flex justify-center'>
                    <AnimationQrcode width={250} />
                  </div>
                  
                  <div className='space-y-6'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>Enter Your Link</label>
                      <input 
                        className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-green-500 focus:outline-none text-lg transition-colors" 
                        type="url" 
                        placeholder="https://example.com" 
                        value={url} 
                        onChange={e => setUrl(e.target.value)} 
                      />
                    </div>
                    
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>Background Color</label>
                        <input 
                          className="w-full h-14 rounded-xl border-2 border-gray-200 cursor-pointer" 
                          type="color" 
                          value={light} 
                          onChange={e => setLight(e.target.value)} 
                        />
                      </div>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>QR Color</label>
                        <input 
                          className="w-full h-14 rounded-xl border-2 border-gray-200 cursor-pointer" 
                          type="color" 
                          value={dark} 
                          onChange={e => setDark(e.target.value)} 
                        />
                      </div>
                    </div>
                    
                    <button 
                      className='w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-lg font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5' 
                      onClick={onQrcode}
                    >
                      Generate QR Code
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className='py-24'>
          <div className='container mx-auto px-6 lg:px-12'>
            <Slide>
              <div className='max-w-4xl mx-auto text-center bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-12 lg:p-16 shadow-2xl'>
                <h2 className='text-4xl lg:text-5xl font-bold text-white mb-6'>
                  Ready to Get Started?
                </h2>
                <p className='text-xl text-green-50 mb-8'>
                  Join thousands of users who are already organizing their links with Lindle
                </p>
                <Link 
                  href="https://chrome.google.com/webstore/detail/igkkojjaikfmiibedalhgmfnjohlhmaj" 
                  target='_blank'
                  className='inline-flex items-center gap-3 px-10 py-5 bg-white text-green-600 text-xl font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1'
                >
                  Start Using Lindle Today
                  <FaArrowRight />
                </Link>
              </div>
            </Slide>
          </div>
        </section>

        {/* Pricing */}
        <section id="priceplans" className="py-24 bg-gradient-to-br from-gray-50 to-green-50/30">
          <div className='container mx-auto px-6 lg:px-12'>
            <div className='text-center mb-16'>
              <h2 className='text-4xl lg:text-5xl font-bold text-gray-900 mb-4'>
                Simple, Transparent Pricing
              </h2>
              <p className='text-xl text-gray-600'>
                Choose the plan that works best for you
              </p>
            </div>

            <Fade damping={1.0}>
              <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto'>
                <PriceCard
                  title="Free Forever"
                  description=""
                  url="https://chrome.google.com/webstore/detail/igkkojjaikfmiibedalhgmfnjohlhmaj"
                  price={0}
                  discount={0}
                  features={FEATURES}
                />
                {priceplans.map(price =>
                  <PriceCard
                    key={price._id}
                    title={price.name}
                    description={price.description}
                    url={`/checkout/buy?id=${price._id}`}
                    price={price.price}
                    discount={price.discount}
                    features={price.features}
                  />
                )}
              </div>
            </Fade>
          </div>
        </section>

        {/* FAQ */}
        <section className='py-24'>
          <div className='container mx-auto px-6 lg:px-12'>
            <div className='max-w-4xl mx-auto'>
              <div className='text-center mb-16'>
                <h2 className='text-4xl lg:text-5xl font-bold text-gray-900 mb-4'>
                  Frequently Asked Questions
                </h2>
                <p className='text-xl text-gray-600'>
                  Everything you need to know about Lindle
                </p>
              </div>

              <div className='bg-white rounded-2xl shadow-lg p-8'>
                <Accordion atomic={true}>
                  {FAQ.map((faq, index) =>
                    <Fade damping={1.0} key={index}>
                      <AccordionItem title={faq.question}>
                        <p className='text-gray-600 leading-relaxed'>{faq.answer}</p>
                      </AccordionItem>
                    </Fade>
                  )}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  )
}